import {
  Button,
  DatePicker,
  FilterPanel,
  Group,
  MultiSelect,
  SearchInput,
  Select,
  Stack,
} from "@assetwatch/ui";
import { useTranslation } from "react-i18next";
import type { MotionAmplificationJob } from "../../clients/motion-amplification";
import {
  getMotionAmplificationFilterOptions,
  hasActiveMotionAmplificationFilters,
  type MotionAmplificationDateRange,
  type MotionAmplificationFilters,
} from "./motionAmplificationFilters";

const dateRangeValues: MotionAmplificationDateRange[] = [
  "all",
  "today",
  "last7Days",
  "last30Days",
  "last90Days",
  "custom",
];

function dateRangeFromValue(
  value: string | null,
): MotionAmplificationDateRange {
  return dateRangeValues.find((dateRange) => dateRange === value) ?? "all";
}

function jobsForCustomers(
  jobs: MotionAmplificationJob[],
  customerIds: string[],
) {
  return jobs.filter((job) =>
    customerIds.includes(job.assignment?.customerId ?? ""),
  );
}

function jobsForFacilities(
  jobs: MotionAmplificationJob[],
  facilityIds: string[],
) {
  return jobs.filter((job) =>
    facilityIds.includes(job.assignment?.facilityId ?? ""),
  );
}

export function MotionAmplificationJobFilters({
  jobs,
  filters,
  onFiltersChange,
}: {
  jobs: MotionAmplificationJob[];
  filters: MotionAmplificationFilters;
  onFiltersChange: (filters: MotionAmplificationFilters) => void;
}) {
  const { t } = useTranslation();
  const options = getMotionAmplificationFilterOptions(jobs);
  const facilityOptions =
    filters.customerIds.length > 0
      ? getMotionAmplificationFilterOptions(
          jobsForCustomers(jobs, filters.customerIds),
        ).facilities
      : [];
  const machineOptions =
    filters.facilityIds.length > 0
      ? getMotionAmplificationFilterOptions(
          jobsForFacilities(jobs, filters.facilityIds),
        ).machines
      : [];
  const hasActiveFilters = hasActiveMotionAmplificationFilters(filters);
  const statusOptions = [
    { value: "UploadPending", label: t("motionAmplification.uploadPending") },
    { value: "Processing", label: t("motionAmplification.processing") },
    { value: "Completed", label: t("motionAmplification.completed") },
  ];
  const dateRangeOptions = [
    { value: "all", label: t("motionAmplification.anyDate") },
    { value: "today", label: t("motionAmplification.today") },
    { value: "last7Days", label: t("motionAmplification.last7Days") },
    { value: "last30Days", label: t("motionAmplification.last30Days") },
    { value: "last90Days", label: t("motionAmplification.last90Days") },
    { value: "custom", label: t("motionAmplification.customRange") },
  ];

  function updateCustomDate(key: "dateStart" | "dateEnd", value: Date | null) {
    const nextFilters = { ...filters, [key]: value };
    if (
      nextFilters.dateStart !== null &&
      nextFilters.dateEnd !== null &&
      nextFilters.dateStart > nextFilters.dateEnd
    ) {
      nextFilters[key === "dateStart" ? "dateEnd" : "dateStart"] = null;
    }
    onFiltersChange(nextFilters);
  }

  function updateCustomers(customerIds: string[]) {
    const nextFacilityOptions = getMotionAmplificationFilterOptions(
      jobsForCustomers(jobs, customerIds),
    ).facilities;
    const facilityIds = filters.facilityIds.filter((facilityId) =>
      nextFacilityOptions.some((option) => option.value === facilityId),
    );
    const nextMachineOptions = getMotionAmplificationFilterOptions(
      jobsForFacilities(jobs, facilityIds),
    ).machines;
    const machineIds = filters.machineIds.filter((machineId) =>
      nextMachineOptions.some((option) => option.value === machineId),
    );
    onFiltersChange({ ...filters, customerIds, facilityIds, machineIds });
  }

  function updateFacilities(facilityIds: string[]) {
    const nextMachineOptions = getMotionAmplificationFilterOptions(
      jobsForFacilities(jobs, facilityIds),
    ).machines;
    const machineIds = filters.machineIds.filter((machineId) =>
      nextMachineOptions.some((option) => option.value === machineId),
    );
    onFiltersChange({ ...filters, facilityIds, machineIds });
  }

  function clearFilters() {
    onFiltersChange({
      searchTerm: "",
      customerIds: [],
      facilityIds: [],
      machineIds: [],
      statuses: [],
      frameRates: [],
      dateRange: "all",
      dateStart: null,
      dateEnd: null,
    });
  }

  return (
    <div className="flex flex-wrap items-end gap-(--spacing-12)">
      <div className="min-w-[280px] flex-1">
        <SearchInput
          aria-label={t("motionAmplification.searchVideos")}
          onChange={(searchTerm) => onFiltersChange({ ...filters, searchTerm })}
          placeholder={t("motionAmplification.searchVideosPlaceholder")}
          value={filters.searchTerm}
        />
      </div>
      <FilterPanel
        title={t("motionAmplification.filters")}
        triggerLabel={t("motionAmplification.filters")}
        align="end"
      >
        <Stack gap={12}>
          <MultiSelect
            label={t("motionAmplification.customer")}
            options={options.customers}
            onChange={updateCustomers}
            placeholder={t("motionAmplification.selectCustomers")}
            reserveSubtextSpace={false}
            value={filters.customerIds}
          />
          <MultiSelect
            label={t("motionAmplification.facility")}
            options={facilityOptions}
            onChange={updateFacilities}
            placeholder={t("motionAmplification.selectFacilities")}
            reserveSubtextSpace={false}
            value={filters.facilityIds}
            disabled={filters.customerIds.length === 0}
          />
          <MultiSelect
            label={t("motionAmplification.machine")}
            options={machineOptions}
            onChange={(machineIds) =>
              onFiltersChange({ ...filters, machineIds })
            }
            placeholder={t("motionAmplification.selectMachines")}
            reserveSubtextSpace={false}
            value={filters.machineIds}
            disabled={filters.facilityIds.length === 0}
          />
          <MultiSelect
            label={t("motionAmplification.status")}
            options={statusOptions}
            onChange={(statuses) => onFiltersChange({ ...filters, statuses })}
            placeholder={t("motionAmplification.selectStatuses")}
            reserveSubtextSpace={false}
            value={filters.statuses}
          />
          <MultiSelect
            label={t("motionAmplification.frameRate")}
            options={options.frameRates.map((frameRate) => ({
              value: String(frameRate),
              label: t("motionAmplification.frameRateOption", { frameRate }),
            }))}
            onChange={(frameRates) =>
              onFiltersChange({
                ...filters,
                frameRates: frameRates.map(Number).filter(Number.isFinite),
              })
            }
            placeholder={t("motionAmplification.selectFrameRates")}
            reserveSubtextSpace={false}
            value={filters.frameRates.map(String)}
          />
          <Select
            label={t("motionAmplification.date")}
            options={dateRangeOptions}
            onChange={(dateRange) =>
              onFiltersChange({
                ...filters,
                dateRange: dateRangeFromValue(dateRange),
                dateStart: dateRange === "custom" ? filters.dateStart : null,
                dateEnd: dateRange === "custom" ? filters.dateEnd : null,
              })
            }
            placeholder={t("motionAmplification.selectDateRange")}
            reserveSubtextSpace={false}
            value={filters.dateRange}
          />
          {filters.dateRange === "custom" && (
            <Group gap={12} align="start">
              <DatePicker
                label={t("motionAmplification.startDate")}
                onValueChange={(dateStart) =>
                  updateCustomDate("dateStart", dateStart)
                }
                value={filters.dateStart}
              />
              <DatePicker
                label={t("motionAmplification.endDate")}
                onValueChange={(dateEnd) =>
                  updateCustomDate("dateEnd", dateEnd)
                }
                value={filters.dateEnd}
              />
            </Group>
          )}
        </Stack>
      </FilterPanel>
      {hasActiveFilters && (
        <Button variant="ghost" onClick={clearFilters}>
          {t("motionAmplification.clearFilters")}
        </Button>
      )}
    </div>
  );
}
